"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { ArrowRight, Camera, Sparkles, Eye, CheckCircle2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const SHOWCASE_SCENARIOS = [
  {
    name: "Sports Car",
    before: "/showcase/images/before5.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "see-1",
        name: "Style A",
        image: "/showcase/images/after51.webp",
        thumbnail: "/showcase/images/after51.webp",
      },
      {
        id: "see-2",
        name: "Style B",
        image: "/showcase/images/after52.webp",
        thumbnail: "/showcase/images/after52.webp",
      },
      {
        id: "see-3",
        name: "Style C",
        image: "/showcase/images/after53.webp",
        thumbnail: "/showcase/images/after53.webp",
      },
    ],
  },
  {
    name: "Sedan",
    before: "/showcase/images/before1.jpg",
    imageClassName: "object-[center_70%]",
    wheels: [
      {
        id: "see-4",
        name: "Style A",
        image: "/showcase/images/after11.webp",
        thumbnail: "/showcase/images/after11.webp",
      },
      {
        id: "see-5",
        name: "Style B",
        image: "/showcase/images/after12.webp",
        thumbnail: "/showcase/images/after12.webp",
      },
      {
        id: "see-6",
        name: "Style C",
        image: "/showcase/images/after13.webp",
        thumbnail: "/showcase/images/after13.webp",
      },
    ],
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Upload your car photo",
    description: "Take a photo of your car from the side or at a 3/4 angle. Phone photos work great.",
  },
  {
    step: "02",
    title: "Choose or upload wheels",
    description: "Pick from our wheel library or upload any wheel image from eBay, retailers, or anywhere online.",
  },
  {
    step: "03",
    title: "See the result instantly",
    description: "AI generates a realistic preview in under 60 seconds. Download HD images with no watermarks.",
  },
];

const FAQS = [
  {
    question: "How do I see wheels on my car?",
    answer:
      "Simply upload a photo of your car and select a wheel. Our AI will generate a photorealistic preview showing exactly how those wheels will look on your car. The whole process takes under 60 seconds.",
  },
  {
    question: "What kind of photo should I use?",
    answer:
      "A side profile or 3/4 angle shot works best. Make sure the wheels are visible and not blocked. Phone photos work great-no professional camera needed.",
  },
  {
    question: "Can I use any wheel image?",
    answer:
      "Yes! Upload wheel images from anywhere-eBay listings, manufacturer websites, Instagram, or take a photo of wheels you see in person. The AI automatically processes any wheel image.",
  },
  {
    question: "How realistic are the previews?",
    answer:
      "Very realistic. The AI matches perspective, lighting, and shadows to make the wheels look like they're actually on your car. Most users can't tell it's a preview.",
  },
  {
    question: "Can I download the images?",
    answer:
      "Yes. Download HD images of your wheel previews with no watermarks. Share them with friends, post on forums, or show your wheel shop.",
  },
  {
    question: "Does this work with any car?",
    answer:
      "Yes-sedans, SUVs, trucks, sports cars, classics, and more. If you can photograph it, we can show you new wheels on it.",
  },
];

export function SeeWheelsOnMyCarContent() {
  const { heroCtaRef, showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "See Wheels On My Car",
    url: "https://thewheelvisualizer.com/see-wheels-on-my-car",
    description:
      "Upload a photo of your car and see how new wheels will look. AI-powered wheel visualization.",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "GraphicsApplication",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to see wheels on your car",
    description: "Preview how new wheels will look on your car using AI visualization.",
    step: [
      {
        "@type": "HowToStep",
        name: "Upload your car photo",
        text: "Take a side or 3/4 angle photo of your car and upload it.",
      },
      {
        "@type": "HowToStep",
        name: "Select wheels",
        text: "Choose from our wheel library or upload your own wheel image.",
      },
      {
        "@type": "HowToStep",
        name: "See the preview",
        text: "AI generates a realistic preview showing how the wheels look on your car.",
      },
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://thewheelvisualizer.com" },
      { "@type": "ListItem", position: 2, name: "See Wheels On My Car", item: "https://thewheelvisualizer.com/see-wheels-on-my-car" },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Eye className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">AI-powered preview</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                See Wheels On
                <br />
                <span className="text-white/70">Your Car</span>
              </h1>
              
              <p className="text-lg text-white/60 mb-4 leading-relaxed">
                Upload a photo of your car and see exactly how new wheels will look. 
                No imagination required-just realistic, photorealistic previews.
              </p>
              <p className="text-white/80 font-medium mb-8">
                Works with any car. Any wheel. Under 60 seconds.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" ref={heroCtaRef}>
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  {ctaText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/credits">View Pricing</Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-8">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">Any Car Photo</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">&lt;60s Results</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">HD Download</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/images/after51.webp"
                  alt="See wheels on my car preview"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Photorealistic results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">How it works</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              See wheels on your car in 3 steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="relative">
                <div className="text-6xl font-bold text-white/5 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-white/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive showcase */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">See it in action</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Drag to compare before & after
            </h2>
            <p className="text-white/60 mt-4">
              Use the slider to see the transformation. Tap thumbnails to try different wheel styles.
            </p>
          </div>

          <div className="space-y-16">
            {SHOWCASE_SCENARIOS.map((scenario, idx) => (
              <div key={scenario.name}>
                <p className="text-sm text-white/50 mb-4">{scenario.name}</p>
                <InteractiveShowcase
                  beforeImage={scenario.before}
                  wheels={scenario.wheels}
                  imageClassName={scenario.imageClassName}
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why use this tool */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Why visualize first</p>
              <h2 className="text-3xl font-semibold text-white mb-6">
                Stop guessing. Start seeing.
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                Wheels are expensive. Before you spend $1,000-$5,000+ on a new set, see exactly how they&apos;ll look on your car. No more imagining, no more returns, no more expensive mistakes.
              </p>
              <ul className="space-y-4">
                {[
                  "See any wheel on YOUR actual car",
                  "Compare multiple styles side by side",
                  "Make confident buying decisions",
                  "Show your wheel shop exactly what you want",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white/70">
                    <CheckCircle2 className="h-4 w-4 text-white/40 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <Image
                src="/showcase/after2.webp"
                alt="Wheel preview example"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Frequently asked questions
            </h2>
            <p className="text-white/60 mt-4">
              Common questions about seeing wheels on your car.
            </p>
          </div>
          <FAQSection faqs={FAQS} />
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
